import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { countryCodes } from "../utils/countryCodes";
import moment from "moment";

const ScheduleBookingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get query parameters
  const serviceId = searchParams.get("serviceId");
  const date = searchParams.get("date");
  const startTime = searchParams.get("startTime");
  const endTime = searchParams.get("endTime");

  // State
  const [loading, setLoading] = useState(true);
  const [service, setService] = useState(null);
  const [provider, setProvider] = useState(null);
  const [error, setError] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    countryCode: "+1",
    phone: "",
    fullName: "",
    email: "",
    notes: "",
  });
  const [formError, setFormError] = useState(null);
  const [isNewClient, setIsNewClient] = useState(false);

  // Fetch service details
  useEffect(() => {
    const fetchService = async () => {
      if (!serviceId) {
        setError("Service ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/services/${serviceId}/public`
        );
        setService(response.data);
        setProvider(response.data.Provider);
      } catch (err) {
        console.error("Error fetching service:", err);
        setError(err.response?.data?.message || "Service not found");
      } finally {
        setLoading(false);
      }
    };

    fetchService();
  }, [serviceId]);

  // Validate query parameters
  useEffect(() => {
    if (!serviceId || !date || !startTime || !endTime) {
      setError("Missing required booking parameters");
      setLoading(false);
    }
  }, [serviceId, date, startTime, endTime]);

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = moment(dateStr, "YYYY-MM-DD");
    return date.format("dddd, MMMM D, YYYY");
  };

  // Format time for display (12-hour format)
  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    // Decode URL-encoded time (e.g., "2%3A30%20PM" -> "2:30 PM")
    const decoded = decodeURIComponent(timeStr);
    return decoded;
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormError(null);
  };

  // Handle booking submission
  const handleBooking = async (e) => {
    e.preventDefault();
    setFormError(null);

    // Validation
    if (!formData.phone.trim()) {
      setFormError("Please enter your phone number");
      return;
    }

    if (isNewClient) {
      if (!formData.fullName.trim()) {
        setFormError("Please enter your full name");
        return;
      }
      if (!formData.email.trim()) {
        setFormError("Please enter your email");
        return;
      }
    }

    setBookingLoading(true);

    try {
      // Step 1: Find or create client
      let client;
      try {
        const clientResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/clients/find-or-create-public`,
          {
            providerId: provider.id,
            countryCode: formData.countryCode || "+1",
            phone: formData.phone.trim(),
            fullName: formData.fullName.trim() || null,
            email: formData.email.trim() || null,
          }
        );
        client = clientResponse.data.client;
      } catch (clientErr) {
        if (clientErr.response?.status === 400 && clientErr.response.data?.requiresFullName) {
          setIsNewClient(true);
          setFormError("Please enter your full name and email to continue");
          setBookingLoading(false);
          return;
        } else if (clientErr.response?.status === 403) {
          setFormError(clientErr.response.data?.message || "You are unable to book with this provider");
          setBookingLoading(false);
          return;
        } else {
          throw clientErr;
        }
      }

      // Step 2: Create booking
      // Convert date and time to proper format (YYYY-MM-DDTHH:mm:ss)
      // startTime and endTime come as "2:30 PM" format (may be URL encoded), need to convert to 24-hour
      const convertTo24Hour = (time12h) => {
        try {
          // Decode URL-encoded time if needed
          let decoded = time12h;
          try {
            decoded = decodeURIComponent(time12h);
          } catch (e) {
            // Already decoded or not URL encoded
            decoded = time12h;
          }
          
          // Parse time (format: "2:30 PM" or "2:30PM")
          const trimmed = decoded.trim();
          const parts = trimmed.split(/\s+/);
          if (parts.length < 2) {
            // Try without space (e.g., "2:30PM")
            const match = trimmed.match(/(\d+):(\d+)(AM|PM)/i);
            if (match) {
              const [, hours, minutes, period] = match;
              let hour24 = parseInt(hours);
              if (period.toUpperCase() === "PM" && hour24 !== 12) hour24 += 12;
              if (period.toUpperCase() === "AM" && hour24 === 12) hour24 = 0;
              return `${String(hour24).padStart(2, "0")}:${minutes.padStart(2, "0")}:00`;
            }
            throw new Error("Invalid time format");
          }
          
          const [time, period] = parts;
          const [hours, minutes] = time.split(":");
          let hour24 = parseInt(hours);
          if (period.toUpperCase() === "PM" && hour24 !== 12) hour24 += 12;
          if (period.toUpperCase() === "AM" && hour24 === 12) hour24 = 0;
          return `${String(hour24).padStart(2, "0")}:${minutes.padStart(2, "0")}:00`;
        } catch (err) {
          console.error("Error converting time:", err, "Input:", time12h);
          throw new Error(`Invalid time format: ${time12h}`);
        }
      };

      const startTime24 = convertTo24Hour(startTime);
      const endTime24 = convertTo24Hour(endTime);
      const startAt = `${date}T${startTime24}`;
      const endAt = `${date}T${endTime24}`;

      const bookingPayload = {
        providerId: provider.id,
        clientId: client.id,
        serviceId: service.id,
        addonIds: [], // No addons for quick booking links
        startAt,
        endAt,
        timeZone: provider.timeZone || "America/New_York",
        notesFromClient: formData.notes.trim() || null,
        channel: "link", // Use "link" as it's a booking created from a link
      };

      const bookingResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/bookings/public`,
        bookingPayload
      );

      // Handle response
      if (bookingResponse.data.paymentLink) {
        // Redirect to payment
        window.location.href = bookingResponse.data.paymentLink;
      } else {
        // Booking confirmed - redirect to confirmation page
        navigate("/booking-confirmation", {
          state: { booking: bookingResponse.data.booking },
        });
      }
    } catch (err) {
      console.error("Booking error:", err);
      let errorMessage = "Booking failed. Please try again.";
      
      if (err.response?.status === 400) {
        errorMessage = err.response.data?.message || "Invalid booking request";
      } else if (err.response?.status === 403) {
        errorMessage = err.response.data?.message || "You are unable to book with this provider";
      } else if (err.response?.status === 404) {
        errorMessage = "Service or provider not found";
      } else if (err.response?.status === 409) {
        errorMessage = "This time slot is already booked. Please select a different time.";
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setFormError(errorMessage);
      setBookingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (error || !service || !provider) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Booking Error</h1>
          <p className="text-gray-600 mb-6">{error || "Service not found"}</p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Complete Your Booking</h1>

          {/* Booking Details (Read-only) */}
          <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Booking Details</h2>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-gray-600">Service:</span>
                <p className="text-gray-900 font-medium">{service.name}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Provider:</span>
                <p className="text-gray-900 font-medium">{provider.businessName}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Date:</span>
                <p className="text-gray-900 font-medium">{formatDate(date)}</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Time:</span>
                <p className="text-gray-900 font-medium">
                  {formatTime(startTime)} - {formatTime(endTime)}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Price:</span>
                <p className="text-gray-900 font-medium">${parseFloat(service.basePrice).toFixed(2)}</p>
              </div>
            </div>
          </div>

          {/* Booking Form */}
          <form onSubmit={handleBooking} className="space-y-6">
            {formError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{formError}</p>
              </div>
            )}

            {/* Policy Notice - Card on File Required */}
            {provider?.requireCardOnFile && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start">
                  <span className="text-amber-600 mr-2">⚠️</span>
                  <div className="text-sm text-amber-800">
                    <p className="font-semibold mb-1">Card Required for This Booking</p>
                    <p className="text-amber-700">
                      This provider requires a card on file. 
                      {provider.cancellationWindowHours > 0 && (
                        <> You may cancel for free up to <strong>{provider.cancellationWindowHours} hour{provider.cancellationWindowHours !== 1 ? 's' : ''}</strong> before your appointment.</>
                      )}
                    </p>
                    {provider.noShowFeeAmount > 0 && (
                      <p className="mt-1 text-amber-700">
                        {provider.noShowFeeType === 'percentage' ? (
                          <>No-shows or late cancellations will be charged <strong>{parseFloat(provider.noShowFeeAmount).toFixed(0)}%</strong> of the booking total.</>
                        ) : (
                          <>No-shows or late cancellations will be charged <strong>${parseFloat(provider.noShowFeeAmount).toFixed(2)}</strong>.</>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Phone Number */}
            <div>
              <label className="block mb-2 text-gray-700 font-medium">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <select
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleInputChange}
                  className="rounded-lg p-3 bg-white border border-gray-300 text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 min-w-[140px]"
                  disabled={bookingLoading}
                >
                  {countryCodes.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.flag} {country.code}
                    </option>
                  ))}
                </select>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="flex-1 rounded-lg p-3 bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="(555) 123-4567"
                  disabled={bookingLoading}
                  required
                />
              </div>
            </div>

            {/* Full Name (shown if new client) */}
            {isNewClient && (
              <div>
                <label className="block mb-2 text-gray-700 font-medium">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="w-full rounded-lg p-3 bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="John Doe"
                  disabled={bookingLoading}
                  required={isNewClient}
                />
              </div>
            )}

            {/* Email (shown if new client) */}
            {isNewClient && (
              <div>
                <label className="block mb-2 text-gray-700 font-medium">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full rounded-lg p-3 bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="john@example.com"
                  disabled={bookingLoading}
                  required={isNewClient}
                />
              </div>
            )}

            {/* Notes (Optional) */}
            <div>
              <label className="block mb-2 text-gray-700 font-medium">Notes (Optional)</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="w-full rounded-lg p-3 bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                rows={3}
                placeholder="Any special requests or notes for your booking..."
                disabled={bookingLoading}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={bookingLoading}
              className="w-full px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {bookingLoading ? (
                <>
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                  Processing...
                </>
              ) : (
                "Complete Booking"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ScheduleBookingPage;

